import { MouseEvent, ReactNode, useRef } from 'react';
import { Modal } from 'antd';
import classNames from 'classnames';
import { Icons } from '@monorepo/icons';
import { IconsNames } from '@monorepo/types';
import { TextComponent } from '../../../data-display';
import { Flex } from '../../../layout';

import './notification-modal.scss';

export enum ModalVariants {
  Error = 'error',
  Warning = 'warning',
  Success = 'success',
  Info = 'info',
}

const modalIcons: Record<ModalVariants, IconsNames> = {
  [ModalVariants.Error]: IconsNames.Fail,
  [ModalVariants.Warning]: IconsNames.Warning,
  [ModalVariants.Success]: IconsNames.Validation,
  [ModalVariants.Info]: IconsNames.Compass,
};

type NotificationModalProps = {
  variant?: ModalVariants;
  isOpen: boolean;
  onOk?: (e: MouseEvent<HTMLButtonElement>) => void;
  onCancel?: (e: MouseEvent<HTMLButtonElement>) => void;
  okText?: string;
  cancelText?: string;
  modalTitle?: ReactNode;
  modalMessage?: ReactNode;
  cancelButtonHidden?: boolean;
  okButtonHidden?: boolean;
  centered?: boolean;
  closable?: boolean;
  destroyOnClose?: boolean;
  maskClosable?: boolean;
  isLoading?: boolean;
  className?: string;
};

export type { NotificationModalProps };

export const NotificationModal = ({
  variant = ModalVariants.Error,
  isOpen,
  onOk,
  onCancel,
  okText = 'Ok',
  cancelText = 'Cancel',
  modalTitle = variant,
  modalMessage,
  cancelButtonHidden = false,
  okButtonHidden = false,
  closable = false,
  centered = true,
  destroyOnClose = true,
  maskClosable = false,
  isLoading = false,
  className,
}: NotificationModalProps) => {
  const wrapRef = useRef(null);
  const iconName = modalIcons[variant];

  return (
    <Modal
      open={isOpen}
      okText={okText}
      onOk={onOk}
      cancelText={cancelText}
      onCancel={onCancel}
      closable={closable}
      maskClosable={maskClosable}
      centered={centered}
      destroyOnClose={destroyOnClose}
      className={classNames(
        'notification-modal',
        { 'no-footer': okButtonHidden && cancelButtonHidden },
        className,
      )}
      okButtonProps={{
        className: 'ok-btn',
        loading: isLoading,
        disabled: isLoading,
        style: { display: okButtonHidden ? 'none' : 'flex' },
      }}
      cancelButtonProps={{
        className: 'cancel-btn',
        loading: isLoading,
        disabled: isLoading,
        style: { display: cancelButtonHidden ? 'none' : 'flex' },
      }}
      wrapProps={{
        ref: wrapRef,
        onClick: (e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();

          if (!maskClosable) return;
          if (e.target !== wrapRef.current) return;

          onCancel?.(e);
        },
      }}
    >
      <Flex vertical gap={12} align="center" justify="center">
        <Icons
          iconName={iconName}
          className={classNames('notification-modal-icon', variant)}
        />

        <TextComponent
          level={2}
          weight={600}
          textAlign="center"
          className="modal-title"
        >
          {modalTitle}
        </TextComponent>

        {modalMessage && (
          <TextComponent level={6} textAlign="center" className="modal-msg">
            {modalMessage}
          </TextComponent>
        )}
      </Flex>
    </Modal>
  );
};
